// G5 : prix réglementés et observations de terrain (charte D-038, « exceptions d'abord »). Les observations
// à instruire viennent en tête ; le circuit (signalée, en instruction, confirmée ou infirmée, transmise) n'avance
// que par décision motivée d'une personne habilitée, jamais automatiquement. La différence avec la référence
// est un simple fait arithmétique, sans qualification. Les prix du jeu fictif sont marqués « illustratif ».

import { useState, type FormEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CODES_PROVINCES, formaterNombre } from '@carburanttrack/shared';
import { api } from '../../api/client';
import type { InstallationListe } from '../../api/types';
import { aUnRole, useSession } from '../../app/session';
import { Indisponible, Message, MessageErreur, Squelette } from '../../composants/Etats';
import { Icone } from '../../composants/Icones';
import { date, dateHeure, libelleProvince } from '../../composants/format';
import '../inspections/terrain.css';

interface ReferentielPrix {
  categories: { code: string; libelle: string; unite: string }[];
  etats: { code: string; libelle: string; suivants: string[] }[];
  origines: { code: string; libelle: string }[];
  produits: { code: string; nom: string; uniteReference: string }[];
}

interface PrixOfficiel {
  id: string;
  produitCode: string;
  categorie: string;
  provinceCode: string | null;
  unite: string;
  prix: string;
  devise: string;
  dateEffet: string;
  referenceTexte: string;
  illustratif: boolean;
  motif: string;
  motifAnnulation: string | null;
  statut: string;
  creeLe: string;
  auteur?: string | null;
}

interface Observation {
  id: string;
  reference: string;
  installationId: string;
  installationNom: string | null;
  provinceCode: string | null;
  produitCode: string;
  categorie: string;
  prixObserve: string;
  observeLe: string;
  origine: string;
  prixOfficielValeur: string | null;
  prixOfficielIllustratif: boolean | null;
  difference: string | null;
  commentaire: string | null;
  etat: string;
}

interface DetailObservation extends Observation {
  historique: { id: string; etatAvant: string; etatApres: string; motif: string; destinataire: string | null; auteur: string | null; creeLe: string }[];
  suivantsPossibles: string[];
}

const ROLES_PUBLICATION = ['administrateur_national'];
const SEGMENTS_OBSERVATIONS: Record<string, string> = { signalee: 'Signalées', en_instruction: 'En instruction', confirmee: 'Confirmées', infirmee: 'Infirmées', transmise: 'Transmises' };
const ROLES_INSTRUCTION = ['administrateur_national', 'directeur_general', 'directeur', 'chef_service'];
const ROLES_RELEVE = ['inspecteur', 'agent_terrain', 'administrateur_national', 'directeur_general'];

function useReferentielPrix() {
  return useQuery({ queryKey: ['prix', 'referentiel'], queryFn: async () => (await api<ReferentielPrix>('/api/v1/prix/referentiel')).donnees, staleTime: 5 * 60_000 });
}

const devise = (d: string) => (d === 'XAF' ? 'FCFA' : d);
const parUnite = (u: string) => (u === 'L' ? 'le litre' : u === 'kg' ? 'le kilogramme' : 'la bouteille');

function Montant({ valeur, deviseCode = 'XAF', unite }: { valeur: string | null; deviseCode?: string; unite?: string }) {
  if (valeur === null) return <Indisponible motif="aucun prix de référence en vigueur à la date de l’observation" />;
  return (
    <span className="valeur">
      {formaterNombre(valeur)} {devise(deviseCode)}
      {unite ? <small> {parUnite(unite)}</small> : null}
    </span>
  );
}

export function PagePrix() {
  const { etat: session } = useSession();
  const [params, setParams] = useSearchParams();
  const ref = useReferentielPrix();
  const peutPublier = aUnRole(session, ...ROLES_PUBLICATION);
  const peutInstruire = aUnRole(session, ...ROLES_INSTRUCTION);
  const peutRelever = aUnRole(session, ...ROLES_RELEVE);
  const lireObservations = aUnRole(session, ...ROLES_INSTRUCTION, 'superviseur', 'inspecteur', 'agent_terrain');
  const onglet = params.get('onglet') ?? (lireObservations ? 'observations' : 'grille');
  const [formulaire, setFormulaire] = useState<'publier' | 'relever' | null>(null);
  const grille = useQuery({ queryKey: ['prix', 'grille'], queryFn: async () => api<PrixOfficiel[]>('/api/v1/prix') });
  const aInstruire = useQuery({
    queryKey: ['prix', 'observations', 'a-instruire'],
    queryFn: async () => (await api<Observation[]>('/api/v1/observations-prix')).donnees.filter((o) => o.etat === 'signalee' || o.etat === 'en_instruction' || o.etat === 'confirmee'),
    enabled: peutInstruire,
  });
  const illustratif = grille.data?.meta.illustratif === true;
  const choisirOnglet = (o: string) => {
    const p = new URLSearchParams(params);
    p.set('onglet', o);
    setParams(p, { replace: true });
  };
  const nomProduit = (c: string) => ref.data?.produits.find((p) => p.code === c)?.nom ?? c;
  const libelleCategorie = (c: string) => ref.data?.categories.find((x) => x.code === c)?.libelle ?? c;
  const libelleEtat = (c: string) => ref.data?.etats.find((x) => x.code === c)?.libelle ?? c;

  const signalees = (aInstruire.data ?? []).filter((o) => o.etat === 'signalee').length;
  const enInstruction = (aInstruire.data ?? []).filter((o) => o.etat === 'en_instruction').length;
  const confirmees = (aInstruire.data ?? []).filter((o) => o.etat === 'confirmee').length;

  const onglets = [
    ...(lireObservations ? [{ cle: 'observations', libelle: 'Observations de terrain' }] : []),
    { cle: 'grille', libelle: 'Prix en vigueur' },
    { cle: 'historique', libelle: 'Historique des prix' },
  ];

  return (
    <div>
      <div className="page-tete">
        <div>
          <h1>Prix et observations</h1>
          <p>Référentiel des prix officiels avec date d’effet, saisi par l’administrateur fonctionnel ; le logiciel n’invente aucun prix.</p>
        </div>
        <div className="actions">
          {peutRelever && (
            <button type="button" className={peutPublier ? 'bouton' : 'bouton primaire'} aria-expanded={formulaire === 'relever'} onClick={() => setFormulaire(formulaire === 'relever' ? null : 'relever')}>
              <Icone nom="formulaire" />
              Relever un prix
            </button>
          )}
          {peutPublier && (
            <button type="button" className="bouton primaire" aria-expanded={formulaire === 'publier'} onClick={() => setFormulaire(formulaire === 'publier' ? null : 'publier')}>
              <Icone nom="ajout" />
              Publier un prix officiel
            </button>
          )}
        </div>
      </div>

      {illustratif && (
        <div className="bloc-insuffisant">
          <b>Prix illustratifs</b>
          <span>Les prix de référence affichés proviennent du jeu de données fictif : valeurs illustratives, sans valeur officielle. Ils sont remplacés par la saisie de l’administrateur fonctionnel.</span>
        </div>
      )}

      {formulaire === 'publier' && ref.data && <PublierPrix referentiel={ref.data} fermer={() => setFormulaire(null)} />}
      {formulaire === 'relever' && ref.data && <ReleverPrix referentiel={ref.data} fermer={() => setFormulaire(null)} />}

      {peutInstruire && signalees + enInstruction + confirmees > 0 && (
        <section className="a-traiter" aria-labelledby="titre-a-instruire">
          <h2 id="titre-a-instruire" className="sur-titre">
            Ce qui demande une action
          </h2>
          <ol className="liste-exceptions">
            {[
              { n: signalees, cle: 'signalee', marque: 'Observations, signalées', titre: `${signalees} observation${signalees > 1 ? 's' : ''} à mettre en instruction`, detail: 'Vérification à engager par une personne habilitée' },
              { n: enInstruction, cle: 'en_instruction', marque: 'Observations, en instruction', titre: `${enInstruction} observation${enInstruction > 1 ? 's' : ''} à conclure`, detail: 'Confirmer ou infirmer, avec motif' },
              { n: confirmees, cle: 'confirmee', marque: 'Observations, confirmées', titre: `${confirmees} observation${confirmees > 1 ? 's' : ''} confirmée${confirmees > 1 ? 's' : ''} à transmettre`, detail: 'Transmission au service compétent, destinataire obligatoire' },
            ]
              .filter((e) => e.n > 0)
              .map((e, i) => (
                <li key={e.cle}>
                  <button
                    type="button"
                    className="exception ambre bouton-exception"
                    onClick={() => {
                      const p = new URLSearchParams(params);
                      p.set('onglet', 'observations');
                      p.set('etat', e.cle);
                      setParams(p, { replace: true });
                    }}
                  >
                    <span className="numero" aria-hidden="true">
                      {i + 1}
                    </span>
                    <span className="texte">
                      <span className="marque-exception">{e.marque}</span>
                      <b>{e.titre}</b>
                      <small>{e.detail}</small>
                    </span>
                    <Icone nom="chevron" />
                  </button>
                </li>
              ))}
          </ol>
        </section>
      )}

      <div className="onglets" role="tablist" aria-label="Prix et observations">
        {onglets.map((o) => (
          <button key={o.cle} type="button" role="tab" id={`onglet-${o.cle}`} aria-selected={onglet === o.cle} aria-controls={`panneau-${o.cle}`} onClick={() => choisirOnglet(o.cle)}>
            {o.libelle}
          </button>
        ))}
      </div>

      {onglet === 'observations' && lireObservations && (
        <div id="panneau-observations" role="tabpanel" aria-labelledby="onglet-observations">
          <Observations referentiel={ref.data} nomProduit={nomProduit} libelleCategorie={libelleCategorie} libelleEtat={libelleEtat} />
        </div>
      )}
      {onglet === 'grille' && (
        <div id="panneau-grille" role="tabpanel" aria-labelledby="onglet-grille">
          {grille.isLoading && <Squelette lignes={5} />}
          {grille.error && <MessageErreur erreur={grille.error} />}
          {grille.data && (
            <>
              <p className="attenue">En vigueur au {dateHeure(String(grille.data.meta.date))}. Un prix provincial prime sur le prix national pour sa province.</p>
              <TableauPrix lignes={grille.data.donnees} nomProduit={nomProduit} libelleCategorie={libelleCategorie} />
            </>
          )}
        </div>
      )}
      {onglet === 'historique' && (
        <div id="panneau-historique" role="tabpanel" aria-labelledby="onglet-historique">
          <HistoriquePrix nomProduit={nomProduit} libelleCategorie={libelleCategorie} peutAnnuler={peutPublier} />
        </div>
      )}
    </div>
  );
}

function TableauPrix({ lignes, nomProduit, libelleCategorie, actions }: { lignes: PrixOfficiel[]; nomProduit: (c: string) => string; libelleCategorie: (c: string) => string; actions?: (p: PrixOfficiel) => ReactNode }) {
  return (
    <div className="tableau-conteneur">
      <table className="tableau lignes-48">
        <caption className="visuellement-cache">Prix officiels</caption>
        <thead>
          <tr>
            <th scope="col">Produit</th>
            <th scope="col">Zone</th>
            <th scope="col" className="nombre">
              Prix
            </th>
            <th scope="col" className="masquable">
              Date d’effet
            </th>
            <th scope="col" className="masquable">
              Texte de référence
            </th>
            {actions && (
              <th scope="col">
                <span className="visuellement-cache">Action</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {lignes.length === 0 && (
            <tr>
              <td colSpan={actions ? 6 : 5} className="attenue">
                Aucun prix officiel saisi : la référence est indisponible tant que l’administrateur fonctionnel ne l’a pas publiée.
              </td>
            </tr>
          )}
          {lignes.map((p) => (
            <tr key={p.id}>
              <td>
                <b>{nomProduit(p.produitCode)}</b>
                <small>{libelleCategorie(p.categorie)}</small>
              </td>
              <td>{p.provinceCode ? libelleProvince(p.provinceCode) : 'National'}</td>
              <td className="nombre">
                <Montant valeur={p.prix} deviseCode={p.devise} unite={p.unite} />
                {p.illustratif && (
                  <small>
                    <span className="mention-illustrative">Illustratif</span>
                  </small>
                )}
                {p.statut !== 'actif' && <small>annulé : {p.motifAnnulation}</small>}
              </td>
              <td className="masquable">{date(p.dateEffet)}</td>
              <td className="masquable">
                {p.referenceTexte}
                {p.auteur && <small>Saisi par {p.auteur}</small>}
              </td>
              {actions && <td className="nombre">{actions(p)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HistoriquePrix({ nomProduit, libelleCategorie, peutAnnuler }: { nomProduit: (c: string) => string; libelleCategorie: (c: string) => string; peutAnnuler: boolean }) {
  const client = useQueryClient();
  const historique = useQuery({ queryKey: ['prix', 'historique'], queryFn: async () => (await api<PrixOfficiel[]>('/api/v1/prix/historique')).donnees });
  const [enAnnulation, setEnAnnulation] = useState<string | null>(null);
  const [motif, setMotif] = useState('');
  const annulation = useMutation({
    mutationFn: async (id: string) => api(`/api/v1/prix/${id}/annulation`, { corps: { motif } }),
    onSuccess: async () => {
      setEnAnnulation(null);
      setMotif('');
      await client.invalidateQueries({ queryKey: ['prix'] });
    },
  });
  if (historique.isLoading) return <Squelette lignes={5} />;
  if (historique.error) return <MessageErreur erreur={historique.error} />;
  return (
    <>
      <p className="attenue">Chaque publication est conservée avec son auteur, sa date d’effet et son motif ; une annulation est motivée et ne supprime rien.</p>
      {annulation.error && <MessageErreur erreur={annulation.error} titre="Annulation refusée" />}
      <TableauPrix
        lignes={historique.data ?? []}
        nomProduit={nomProduit}
        libelleCategorie={libelleCategorie}
        actions={
          peutAnnuler
            ? (p) =>
                p.statut !== 'actif' ? null : enAnnulation === p.id ? (
                  <form
                    className="action-motivee"
                    onSubmit={(e: FormEvent) => {
                      e.preventDefault();
                      annulation.mutate(p.id);
                    }}
                  >
                    <label htmlFor={`motif-${p.id}`} className="visuellement-cache">
                      Motif de l’annulation
                    </label>
                    <input id={`motif-${p.id}`} placeholder="Motif de l’annulation" value={motif} onChange={(e) => setMotif(e.target.value)} />
                    <div className="actions">
                      <button type="button" className="bouton discret" onClick={() => setEnAnnulation(null)}>
                        Garder
                      </button>
                      <button type="submit" className="bouton danger" disabled={motif.trim().length < 3 || annulation.isPending}>
                        Annuler ce prix
                      </button>
                    </div>
                  </form>
                ) : (
                  <button type="button" className="bouton" onClick={() => setEnAnnulation(p.id)}>
                    Annuler
                  </button>
                )
            : undefined
        }
      />
    </>
  );
}

function Observations({ referentiel, nomProduit, libelleCategorie, libelleEtat }: { referentiel: ReferentielPrix | undefined; nomProduit: (c: string) => string; libelleCategorie: (c: string) => string; libelleEtat: (c: string) => string }) {
  const [params, setParams] = useSearchParams();
  const etat = params.get('etat') ?? '';
  const province = params.get('province') ?? '';
  const [choisie, setChoisie] = useState<string | null>(null);
  const filtres = new URLSearchParams();
  if (etat) filtres.set('etat', etat);
  if (province) filtres.set('province', province);
  const liste = useQuery({ queryKey: ['prix', 'observations', filtres.toString()], queryFn: async () => (await api<Observation[]>(`/api/v1/observations-prix?${filtres.toString()}`)).donnees });
  const changer = (cle: string, valeur: string) => {
    const p = new URLSearchParams(params);
    if (valeur) p.set(cle, valeur);
    else p.delete(cle);
    setParams(p, { replace: true });
  };
  const lignes = liste.data ?? [];
  const selection = lignes.find((o) => o.id === choisie) ?? null;
  return (
    <>
      <div className="barre-filtres" role="search" aria-label="Filtres des observations">
        <div className="segments" role="radiogroup" aria-label="État de l’instruction">
          {[{ code: '', libelle: 'Toutes' }, ...(referentiel?.etats ?? []).map((e) => ({ code: e.code, libelle: SEGMENTS_OBSERVATIONS[e.code] ?? e.libelle }))].map((e) => (
            <button key={e.code || 'toutes'} type="button" role="radio" aria-checked={etat === e.code} onClick={() => changer('etat', e.code)}>
              {e.libelle}
            </button>
          ))}
        </div>
        <div className="champ">
          <label htmlFor="obs-province">Province</label>
          <select id="obs-province" value={province} onChange={(e) => changer('province', e.target.value)}>
            <option value="">Toutes</option>
            {CODES_PROVINCES.map((c) => (
              <option key={c} value={c}>
                {libelleProvince(c)}
              </option>
            ))}
          </select>
        </div>
      </div>
      {liste.isLoading && <Squelette lignes={5} />}
      {liste.error && <MessageErreur erreur={liste.error} />}
      {liste.data && (
        <div className="deux-colonnes">
          <section className="principal" aria-label="Observations">
            <div className="tableau-conteneur">
              <table className="tableau lignes-48">
                <caption className="visuellement-cache">Observations de prix</caption>
                <thead>
                  <tr>
                    <th scope="col">Installation</th>
                    <th scope="col" className="nombre">
                      Prix observé
                    </th>
                    <th scope="col" className="nombre masquable">
                      Référence
                    </th>
                    <th scope="col" className="nombre masquable">
                      Différence
                    </th>
                    <th scope="col">État</th>
                  </tr>
                </thead>
                <tbody>
                  {lignes.length === 0 && (
                    <tr>
                      <td colSpan={5} className="attenue">
                        Aucune observation ne correspond à ces critères dans votre périmètre.
                      </td>
                    </tr>
                  )}
                  {lignes.map((o) => (
                    <tr key={o.id} aria-selected={selection?.id === o.id}>
                      <td>
                        <button type="button" className="bouton-ligne" aria-pressed={selection?.id === o.id} onClick={() => setChoisie(o.id)}>
                          {o.installationNom ?? 'Installation'}
                          <small>
                            {nomProduit(o.produitCode)}, {libelleCategorie(o.categorie)}, le {date(o.observeLe)}
                          </small>
                        </button>
                      </td>
                      <td className="nombre">
                        <Montant valeur={o.prixObserve} />
                      </td>
                      <td className="nombre masquable">
                        {o.prixOfficielValeur === null ? <Indisponible motif="aucune référence" /> : <Montant valeur={o.prixOfficielValeur} />}
                        {o.prixOfficielIllustratif && (
                          <small>
                            <span className="mention-illustrative">Illustratif</span>
                          </small>
                        )}
                      </td>
                      <td className="nombre masquable">{o.difference === null ? <Indisponible motif="sans référence" /> : `${Number(o.difference) > 0 ? '+' : ''}${formaterNombre(o.difference)}`}</td>
                      <td>
                        <span className={o.etat === 'signalee' || o.etat === 'en_instruction' ? 'etiquette ambre' : 'etiquette'}>{libelleEtat(o.etat).split(' (')[0]}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="attenue espace-haut">La différence est un écart arithmétique entre le prix observé et la référence en vigueur à la date de l’observation ; elle ne qualifie rien.</p>
          </section>
          {selection && (
            <aside className="lateral collant" aria-label="Instruction de l’observation">
              <DetailObservationPanneau id={selection.id} referentiel={referentiel} nomProduit={nomProduit} libelleEtat={libelleEtat} />
            </aside>
          )}
        </div>
      )}
    </>
  );
}

function DetailObservationPanneau({ id, referentiel, nomProduit, libelleEtat }: { id: string; referentiel: ReferentielPrix | undefined; nomProduit: (c: string) => string; libelleEtat: (c: string) => string }) {
  const client = useQueryClient();
  const detail = useQuery({ queryKey: ['prix', 'observation', id], queryFn: async () => (await api<DetailObservation>(`/api/v1/observations-prix/${id}`)).donnees });
  const [vers, setVers] = useState('');
  const [motif, setMotif] = useState('');
  const [destinataire, setDestinataire] = useState('');
  const instruction = useMutation({
    mutationFn: async () => api(`/api/v1/observations-prix/${id}/instruction`, { corps: { etat: vers, motif, ...(vers === 'transmise' ? { destinataire } : {}) } }),
    onSuccess: async () => {
      setVers('');
      setMotif('');
      setDestinataire('');
      await client.invalidateQueries({ queryKey: ['prix'] });
    },
  });
  if (detail.isLoading) return <Squelette lignes={5} />;
  if (detail.error || !detail.data) return <MessageErreur erreur={detail.error} />;
  const o = detail.data;
  const origine = referentiel?.origines.find((x) => x.code === o.origine)?.libelle ?? o.origine;
  return (
    <section className="carte-bloc" aria-labelledby="titre-observation">
      <span className={o.etat === 'signalee' || o.etat === 'en_instruction' ? 'etiquette ambre' : 'etiquette'}>{libelleEtat(o.etat)}</span>
      <h2 id="titre-observation">{o.installationNom}</h2>
      <dl className="paires">
        <div>
          <dt>Référence</dt>
          <dd>{o.reference}</dd>
        </div>
        <div>
          <dt>Produit</dt>
          <dd>{nomProduit(o.produitCode)}</dd>
        </div>
        <div>
          <dt>Observé le</dt>
          <dd>{dateHeure(o.observeLe)}</dd>
        </div>
        <div>
          <dt>Origine</dt>
          <dd>{origine}</dd>
        </div>
        {o.commentaire && (
          <div>
            <dt>Commentaire</dt>
            <dd>{o.commentaire}</dd>
          </div>
        )}
      </dl>
      <h3 className="espace-haut">Instruction</h3>
      <ol className="historique">
        {o.historique.map((h) => (
          <li key={h.id}>
            <time dateTime={h.creeLe}>{dateHeure(h.creeLe)}</time>
            <span>
              {libelleEtat(h.etatApres).split(' (')[0]}, {h.auteur ?? 'auteur non lisible'} : {h.motif}
              {h.destinataire ? ` (transmise à ${h.destinataire})` : ''}
            </span>
          </li>
        ))}
      </ol>
      {o.suivantsPossibles.length > 0 && (
        <form
          className="encart"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            instruction.mutate();
          }}
        >
          <h3>Décision</h3>
          <div className="segments" role="radiogroup" aria-label="Nouvel état">
            {o.suivantsPossibles.map((s) => (
              <button key={s} type="button" role="radio" aria-checked={vers === s} onClick={() => setVers(s)}>
                {libelleEtat(s).split(' (')[0]}
              </button>
            ))}
          </div>
          <div className="champ espace-haut">
            <label htmlFor="obs-motif">Motif</label>
            <textarea id="obs-motif" rows={2} value={motif} onChange={(e) => setMotif(e.target.value)} />
          </div>
          {vers === 'transmise' && (
            <div className="champ">
              <label htmlFor="obs-destinataire">Service destinataire</label>
              <input id="obs-destinataire" value={destinataire} onChange={(e) => setDestinataire(e.target.value)} />
            </div>
          )}
          {instruction.error && <MessageErreur erreur={instruction.error} titre="Décision refusée" />}
          <button type="submit" className="bouton primaire" disabled={!vers || motif.trim().length < 3 || (vers === 'transmise' && destinataire.trim().length < 3) || instruction.isPending}>
            Enregistrer la décision
          </button>
        </form>
      )}
    </section>
  );
}

function PublierPrix({ referentiel, fermer }: { referentiel: ReferentielPrix; fermer: () => void }) {
  const client = useQueryClient();
  const [produitCode, setProduit] = useState('');
  const [categorie, setCategorie] = useState('');
  const [provinceCode, setProvince] = useState('');
  const [prix, setPrix] = useState('');
  const [dateEffet, setDateEffet] = useState('');
  const [referenceTexte, setReference] = useState('');
  const [motif, setMotif] = useState('');
  const produit = referentiel.produits.find((p) => p.code === produitCode);
  const gpl = produit?.uniteReference === 'kg';
  const categories = referentiel.categories.filter((c) => (gpl ? c.unite === 'bouteille' : c.unite !== 'bouteille'));
  const envoi = useMutation({
    mutationFn: async () => api('/api/v1/prix', { corps: { produitCode, categorie, ...(provinceCode ? { provinceCode } : {}), prix: prix.replace(',', '.'), dateEffet: `${dateEffet}:00+01:00`, referenceTexte, motif } }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['prix'] });
      fermer();
    },
  });
  const pret = produitCode && categorie && /^\d+([.,]\d{1,2})?$/.test(prix) && dateEffet && referenceTexte.trim().length >= 3 && motif.trim().length >= 3;
  return (
    <form
      className="carte-bloc carte-formulaire"
      aria-labelledby="titre-publier"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        if (pret) envoi.mutate();
      }}
    >
      <h2 id="titre-publier">Publier un prix officiel</h2>
      <p className="attenue">Saisie réelle de l’administrateur fonctionnel, avec le texte qui la fonde. Une date d’effet future programme le prix sans modifier celui en vigueur.</p>
      <div className="formulaire-ligne">
        <div className="champ">
          <label htmlFor="pp-produit">Produit</label>
          <select
            id="pp-produit"
            value={produitCode}
            onChange={(e) => {
              setProduit(e.target.value);
              setCategorie('');
            }}
          >
            <option value="">Choisir</option>
            {referentiel.produits.map((p) => (
              <option key={p.code} value={p.code}>
                {p.nom}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="pp-categorie">Catégorie de prix</label>
          <select id="pp-categorie" value={categorie} onChange={(e) => setCategorie(e.target.value)} disabled={!produitCode}>
            <option value="">Choisir</option>
            {categories.map((c) => (
              <option key={c.code} value={c.code}>
                {c.libelle}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="pp-province">Zone</label>
          <select id="pp-province" value={provinceCode} onChange={(e) => setProvince(e.target.value)}>
            <option value="">National</option>
            {CODES_PROVINCES.map((c) => (
              <option key={c} value={c}>
                {libelleProvince(c)}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="formulaire-ligne">
        <div className="champ">
          <label htmlFor="pp-prix">Prix en FCFA {categorie ? parUnite(referentiel.categories.find((c) => c.code === categorie)?.unite ?? '') : ''}</label>
          <input id="pp-prix" inputMode="decimal" value={prix} onChange={(e) => setPrix(e.target.value.trim())} />
        </div>
        <div className="champ">
          <label htmlFor="pp-date">Date d’effet (heure du Gabon)</label>
          <input id="pp-date" type="datetime-local" value={dateEffet} onChange={(e) => setDateEffet(e.target.value)} />
        </div>
      </div>
      <div className="champ">
        <label htmlFor="pp-reference">Texte de référence (arrêté, décision)</label>
        <input id="pp-reference" value={referenceTexte} onChange={(e) => setReference(e.target.value)} />
      </div>
      <div className="champ">
        <label htmlFor="pp-motif">Motif</label>
        <textarea id="pp-motif" rows={2} value={motif} onChange={(e) => setMotif(e.target.value)} />
      </div>
      {envoi.error && <MessageErreur erreur={envoi.error} titre="Publication refusée" />}
      <div className="actions">
        <button type="button" className="bouton discret" onClick={fermer}>
          Annuler
        </button>
        <button type="submit" className="bouton primaire" disabled={!pret || envoi.isPending}>
          Publier
        </button>
      </div>
    </form>
  );
}

function ReleverPrix({ referentiel, fermer }: { referentiel: ReferentielPrix; fermer: () => void }) {
  const client = useQueryClient();
  const [identifiantClient] = useState(() => crypto.randomUUID());
  const [q, setQ] = useState('');
  const [recherche, setRecherche] = useState('');
  const [installation, setInstallation] = useState<InstallationListe | null>(null);
  const [produitCode, setProduit] = useState('');
  const [categorie, setCategorie] = useState('');
  const [prixObserve, setPrix] = useState('');
  const [observeLe, setObserveLe] = useState(() => new Date(Date.now() + 3_600_000).toISOString().slice(0, 16));
  const [commentaire, setCommentaire] = useState('');
  const [succes, setSucces] = useState(false);
  const installations = useQuery({
    queryKey: ['installations', 'releve-prix', recherche],
    enabled: recherche.length >= 2,
    queryFn: async () => (await api<InstallationListe[]>(`/api/v1/installations?q=${encodeURIComponent(recherche)}&categorie=station&limite=20`)).donnees,
  });
  const produit = referentiel.produits.find((p) => p.code === produitCode);
  const gpl = produit?.uniteReference === 'kg';
  const categories = referentiel.categories.filter((c) => (gpl ? c.unite === 'bouteille' : c.unite !== 'bouteille'));
  const envoi = useMutation({
    mutationFn: async () => api('/api/v1/observations-prix', { corps: { identifiantClient, installationId: installation!.id, produitCode, categorie, prixObserve: prixObserve.replace(',', '.'), observeLe: `${observeLe}:00+01:00`, ...(commentaire.trim() ? { commentaire } : {}) } }),
    onSuccess: async () => {
      setSucces(true);
      await client.invalidateQueries({ queryKey: ['prix'] });
    },
  });
  const pret = installation && produitCode && categorie && /^\d+([.,]\d{1,2})?$/.test(prixObserve) && observeLe;
  if (succes)
    return (
      <Message type="succes">
        Observation enregistrée à l’état « signalée ». Son instruction relève d’une personne habilitée de la DGH.{' '}
        <button type="button" className="bouton discret" onClick={fermer}>
          Fermer
        </button>
      </Message>
    );
  return (
    <form
      className="carte-bloc carte-formulaire"
      aria-labelledby="titre-relever"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        if (pret) envoi.mutate();
      }}
    >
      <h2 id="titre-relever">Relever un prix affiché</h2>
      <p className="attenue">Le relevé est enregistré « signalé » avec la référence en vigueur à la date d’observation ; aucune qualification n’est déduite de l’écart.</p>
      <div className="champ">
        <label htmlFor="rp-q">Station (deux lettres au moins)</label>
        <input id="rp-q" type="search" value={q} onChange={(e) => setQ(e.target.value)} onBlur={() => setRecherche(q.trim())} />
      </div>
      {installations.data && installations.data.length > 0 && (
        <ul className="choix-installation" aria-label="Stations trouvées">
          {installations.data.map((i) => (
            <li key={i.id}>
              <label>
                <input type="radio" name="rp-installation" checked={installation?.id === i.id} onChange={() => setInstallation(i)} />
                <span>
                  {i.nom}
                  <small>
                    {i.organisationNom}, {libelleProvince(i.provinceCode)}
                  </small>
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
      <div className="formulaire-ligne">
        <div className="champ">
          <label htmlFor="rp-produit">Produit</label>
          <select
            id="rp-produit"
            value={produitCode}
            onChange={(e) => {
              setProduit(e.target.value);
              setCategorie('');
            }}
          >
            <option value="">Choisir</option>
            {referentiel.produits.map((p) => (
              <option key={p.code} value={p.code}>
                {p.nom}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="rp-categorie">Catégorie</label>
          <select id="rp-categorie" value={categorie} onChange={(e) => setCategorie(e.target.value)} disabled={!produitCode}>
            <option value="">Choisir</option>
            {categories.map((c) => (
              <option key={c.code} value={c.code}>
                {c.libelle}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="rp-prix">Prix affiché en FCFA</label>
          <input id="rp-prix" inputMode="decimal" value={prixObserve} onChange={(e) => setPrix(e.target.value.trim())} />
        </div>
        <div className="champ">
          <label htmlFor="rp-date">Observé le</label>
          <input id="rp-date" type="datetime-local" value={observeLe} onChange={(e) => setObserveLe(e.target.value)} />
        </div>
      </div>
      <div className="champ">
        <label htmlFor="rp-commentaire">Commentaire (facultatif)</label>
        <textarea id="rp-commentaire" rows={2} value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
      </div>
      {envoi.error && <MessageErreur erreur={envoi.error} titre="Relevé refusé" />}
      <div className="actions">
        <button type="button" className="bouton discret" onClick={fermer}>
          Annuler
        </button>
        <button type="submit" className="bouton primaire" disabled={!pret || envoi.isPending}>
          Enregistrer le relevé
        </button>
      </div>
    </form>
  );
}
